/* eslint-disable react-hooks/exhaustive-deps */

import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useEffect, Fragment } from "react";
import { motion } from "framer-motion";
import useOdooApi from "../hooks/useOdooApi";
import LoadingPage from "./LoadingPage";
import type { Voucher } from "../types/voucher";

import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SendVoucherPopup from "../components/SendVoucherPopup";

export default function VoucherPage() {
  const { getVouchers, revokeVoucher } = useOdooApi();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const [openRevoke, setOpenRevoke] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [loadingRevoke, setLoadingRevoke] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);

  const [openSend, setOpenSend] = useState(false);
  const [selectVoucherToSend, setSelectVoucherToSend] =
    useState<Voucher | null>(null);

  const openPopVoucher = (selectVoucher: Voucher) => {
    setSelectVoucherToSend(selectVoucher);
    setOpenSend(true);
  };

  const openRevokePopup = (code: string) => {
    setSelectedCode(code);
    setOpenRevoke(true);
  };

  const handleRevokeVoucher = async () => {
    if (!user?.patient_id || !selectedCode) return;

    try {
      setLoadingRevoke(true);

      const response = await revokeVoucher({
        patient_id: user.patient_id,
        code: selectedCode,
      });


      if (Number(response.code) >= 300) {
        setOpenRevoke(false)
        setErrorPopup(true)
        return;
      }

      // refetch ใหม่
      const refresh = await getVouchers({
        patient_id: Number(user.patient_id),
      });

      if (refresh.code === 200) {
        setVouchers((refresh?.data as any) ?? []);
      }

      setOpenRevoke(false);
      setSelectedCode(null);
    } catch (error) {
      setOpenRevoke(false)
      setErrorPopup(true)
      console.error("Revoke voucher failed:", error);
    } finally {
      setLoadingRevoke(false);
    }
  };

  useEffect(() => {
    if (!user.patient_id) {
      setLoading(false);
      setError("ไม่พบข้อมูลผู้ใช้");
      return;
    }

    const fetchVouchers = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getVouchers({
          patient_id: Number(user.patient_id),
        });

        if (response.code === 200) {
          setVouchers((response?.data as any) ?? []);
        } else {
          setError(response.message || "ไม่สามารถดึงข้อมูล Voucher ได้");
        }
      } catch (err) {
        console.error("Failed to fetch vouchers:", err);
        setError("เกิดข้อผิดพลาดในการดึงข้อมูล Voucher");
      } finally {
        setLoading(false);
      }
    };

    fetchVouchers();
  }, [user.patient_id]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Box className="body-content" px={2} py={3}>
        <Typography
          variant="h5"
          sx={{ color: "#7479AE", fontWeight: 700, mb: 2 }}
        >
          Vouchers
        </Typography>
        <Typography
          sx={{
            color: "#ff6b6b",
            fontWeight: 400,
            fontSize: 16,
            textAlign: "center",
            py: 4,
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Fragment>
      <Box px={2} py={3}>
        <Typography fontSize={22} fontWeight={700} mb={3}>
          Voucher ของฉัน
        </Typography>

        <Box display="flex" flexDirection="column" gap={1}>
          {vouchers.map((voucher) => (
            <VoucherCard
              key={voucher.code}
              voucher={voucher}
              onOpen={() => openPopVoucher(voucher)}
              onRevoke={openRevokePopup}
            />
          ))}
        </Box>
      </Box>

      <SendVoucherPopup
        open={openSend}
        onClose={() => setOpenSend(false)}
        voucherData={selectVoucherToSend}
        onConfirm={() => { }}
      />

      <Dialog
        open={openRevoke}
        onClose={() => !loadingRevoke && setOpenRevoke(false)}
      >
        <DialogTitle fontWeight={600}>
          ยืนยันการยกเลิก
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Typography>
            คุณต้องการยกเลิกการส่งของขวัญใช่หรือไม่?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenRevoke(false)}
            disabled={loadingRevoke}
            sx={{ textTransform: "none" }}
          >
            ยกเลิก
          </Button>

          <Button
            variant="contained"
            color="error"
            disabled={loadingRevoke}
            onClick={handleRevokeVoucher}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {loadingRevoke ? "กำลังดำเนินการ..." : "ตกลง"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={errorPopup}
        onClose={() => setErrorPopup(false)}
      >
        <DialogTitle fontWeight={600}>
          เกิดข้อผิดพลาด
        </DialogTitle>

        <DialogContent>
          <Typography>
            มีข้อผิดพลาดในการยกเลิก Voucher กรุณาลองใหม่อีกครั้ง
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setErrorPopup(false)}
            variant="contained"
            color="error"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            ปิด
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

function VoucherCard({
  voucher,
  onOpen,
  onRevoke,
}: {
  voucher: Voucher;
  onOpen: () => void;
  onRevoke: (code: string) => void;
}) {
  const navigate = useNavigate();

  const formatDate = (value: string): string => {
    if (!value || value === "-") return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const statusConfig = {
    activated: {
      label: "ใช้งานได้",
      bg: "#EFB699",
      color: "#fff",
    },
    redeemed: {
      label: "ใช้งานแล้ว",
      bg: "#E5E7EB",
      color: "#6B7280",
    },
    booked: {
      label: "จองแล้ว",
      bg: "#E5E7EB",
      color: "#6B7280",
    },
    expired: {
      label: "หมดอายุ",
      bg: "#FEE2E2",
      color: "#991B1B",
    },
    issued: {
      label: "รอดำเนินการ",
      bg: "#E0E7FF",
      color: "#3730A3",
    },
    gifted: {
      label: "ส่งของขวัญแล้ว",
      bg: "#E5E7EB",
      color: "#4B5563",
    },
  }[voucher.state];

  const navigateRedeem = (voucher_code:string)=>{
    localStorage.setItem("voucher_code", voucher_code);
    navigate("/packages")
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      sx={{
        borderRadius: 4,
        border: "1px solid #E5E7EB",
        px: 3,
        py: 2,
        position: "relative",
        bgcolor: "#fff",
        boxShadow: "0 3px 10px rgba(0,0,0,0.05)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 6,
          bgcolor: "#3F4E8C",
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
        }}
      />

      <Box display="flex" justifyContent="space-between" alignItems="start">
        <Box>
          <Typography fontWeight={700} fontSize={18}>
            {voucher.name}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {voucher.code}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 0.5,
            borderRadius: 20,
            fontSize: 12,
            bgcolor: statusConfig?.bg,
            color: statusConfig?.color,
            fontWeight: 500,
          }}
        >
          {statusConfig?.label}
        </Box>
      </Box>

      <Box
        mt={1}
        display="flex"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        fontSize={13}
      >
        <Box>
        สามารถแลกรับสิทธิ์และเข้าใช้บริการได้ภายในวันที่ : <b>{formatDate(voucher?.expiration_date)}</b>
        </Box>

        {voucher.gift_type === "send" && voucher.email && (
          <Box width="100%">
            ส่งให้: {voucher.email}
          </Box>
        )}
      </Box>

      <Box mt={1} display="flex" gap={1} flexWrap="wrap">
        {voucher.state === "activated" && (
          <>
            <Button
              onClick={() => navigateRedeem(voucher.code)}
              variant="outlined"
              startIcon={<ConfirmationNumberIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#3F4E8C",
                color: "#3F4E8C",
              }}
            >
              Redeem
            </Button>
            
            {(voucher.gift_type !== 'send' && voucher.gift_type !== 'receive') && (
              <Button
                onClick={onOpen}
                variant="outlined"
                startIcon={<CardGiftcardIcon />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderColor: "#f5a39c",
                  color: "#f5a39c",
                }}
              >
                Send Gift
              </Button>
            )}


          </>
        )}

        {voucher.state === "gifted" && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelOutlinedIcon />}
            onClick={() => onRevoke(voucher.code)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            ยกเลิก Gift
          </Button>
        )}
      </Box>
    </Box>
  );
}